import { useContext, useEffect, useRef, useState } from "react";
import { TigerContext } from "@shared/context/mobile/TigerContext";
import styles from "./style.module.scss";

// Banner global de estado de conexión para la app de campo.
// - Muestra una barra fija arriba cuando NO hay conexión.
// - Al reconectar, muestra brevemente "Conexión restablecida" en verde y se oculta.
// Detección: window.navigator.onLine + listeners 'online'/'offline' como base robusta,
// y si está disponible el estado nativo de Tiger (tiger.isOnline) lo prioriza.
const MS_OCULTAR_RECONEXION = 3000;

const ConnectionBanner = () => {
  // Tiger puede no estar montado (apps que no usan el contexto): lo leemos sin romper.
  const tiger = useContext(TigerContext);
  const tigerOnline = tiger?.isOnline?.value;

  // Estado base del navegador (siempre disponible).
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  // Si Tiger reporta estado, manda Tiger; si no, usamos el del navegador.
  const isOnline = typeof tigerOnline === "boolean" ? tigerOnline : browserOnline;

  const [mostrarReconexion, setMostrarReconexion] = useState(false);
  const estabaOfflineRef = useRef(false);
  const timeoutRef = useRef(null);

  // Listeners del navegador como fuente base robusta.
  useEffect(() => {
    const handleOnline = () => setBrowserOnline(true);
    const handleOffline = () => setBrowserOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Transición offline -> online: mostrar "Conexión restablecida" y ocultar luego.
  useEffect(() => {
    if (!isOnline) {
      estabaOfflineRef.current = true;
      setMostrarReconexion(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    if (estabaOfflineRef.current) {
      estabaOfflineRef.current = false;
      setMostrarReconexion(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setMostrarReconexion(false);
      }, MS_OCULTAR_RECONEXION);
    }
  }, [isOnline]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Sin conexión: barra persistente. Reconectado: barra transitoria. En línea: nada.
  if (isOnline && !mostrarReconexion) return null;

  const offline = !isOnline;

  return (
    <div
      className={`${styles.banner} ${offline ? styles.offline : styles.online}`}
      role="status"
      aria-live="polite"
    >
      <i className={`bi ${offline ? "bi-wifi-off" : "bi-wifi"} ${styles.icono}`} />
      <span className={styles.texto}>
        {offline
          ? "Sin conexión — los cambios se guardan y se envían al reconectar"
          : "Conexión restablecida"}
      </span>
    </div>
  );
};

export default ConnectionBanner;
